<template>
  <div class="command-details">
    <div class="row">
      <div class="col-12">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h2 class="mb-0">Command Details</h2>
          <base-button type="secondary" @click="$router.go(-1)">
            <i class="fas fa-arrow-left"></i> Back
          </base-button>
        </div>

        <div v-if="loading" class="text-center">
          <i class="fas fa-spinner fa-spin fa-3x"></i>
          <p class="mt-2">Loading command details...</p>
        </div>

        <div v-else-if="commandDetails.command_name">
          <!-- Command Header -->
          <card class="mb-4">
            <div class="text-center">
              <h3 class="text-info">{{ commandDetails.command_name }}</h3>
              <h5 class="text-muted">{{ commandDetails.formatted_name }}</h5>
            </div>
          </card>

          <!-- Summary Statistics -->
          <div class="row mb-4">
            <div class="col-lg-3 col-md-6">
              <card type="card-stats" class="gradient-primary">
                <div class="row">
                  <div class="col-5 col-md-4">
                    <div class="icon-big text-center">
                      <i class="fas fa-chart-bar text-white"></i>
                    </div>
                  </div>
                  <div class="col-7 col-md-8">
                    <div class="numbers">
                      <p class="card-category">Total Runs</p>
                      <p class="card-title">{{ commandDetails.summary.total_runs }}</p>
                    </div>
                  </div>
                </div>
              </card>
            </div>
            <div class="col-lg-3 col-md-6">
              <card type="card-stats" class="gradient-success">
                <div class="row">
                  <div class="col-5 col-md-4">
                    <div class="icon-big text-center">
                      <i class="fas fa-check-circle text-white"></i>
                    </div>
                  </div>
                  <div class="col-7 col-md-8">
                    <div class="numbers">
                      <p class="card-category">Success Rate</p>
                      <p class="card-title">{{ commandDetails.summary.success_rate }}%</p>
                    </div>
                  </div>
                </div>
              </card>
            </div>
            <div class="col-lg-3 col-md-6">
              <card type="card-stats" class="gradient-danger">
                <div class="row">
                  <div class="col-5 col-md-4">
                    <div class="icon-big text-center">
                      <i class="fas fa-times-circle text-white"></i>
                    </div>
                  </div>
                  <div class="col-7 col-md-8">
                    <div class="numbers">
                      <p class="card-category">Failed Runs</p>
                      <p class="card-title">{{ commandDetails.summary.failed_count }}</p>
                    </div>
                  </div>
                </div>
              </card>
            </div>
            <div class="col-lg-3 col-md-6">
              <card type="card-stats" class="gradient-warning">
                <div class="row">
                  <div class="col-5 col-md-4">
                    <div class="icon-big text-center">
                      <i class="fas fa-stopwatch text-white"></i>
                    </div>
                  </div>
                  <div class="col-7 col-md-8">
                    <div class="numbers">
                      <p class="card-category">Avg Duration</p>
                      <p class="card-title">{{ formatDuration(commandDetails.summary.avg_duration_seconds) }}</p>
                    </div>
                  </div>
                </div>
              </card>
            </div>
          </div>

          <!-- Detailed Statistics -->
          <card class="mb-4">
            <h4 slot="header" class="card-title">Detailed Statistics</h4>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Period:</strong></label>
                  <p>Last {{ commandDetails.period_hours }} hours</p>
                </div>
                <div class="form-group">
                  <label><strong>Success Count:</strong></label>
                  <p>{{ commandDetails.summary.success_count }}</p>
                </div>
                <div class="form-group">
                  <label><strong>Running Count:</strong></label>
                  <p>{{ commandDetails.summary.running_count }}</p>
                </div>
                <div class="form-group">
                  <label><strong>Interrupted Count:</strong></label>
                  <p>{{ commandDetails.summary.interrupted_count }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Min Duration:</strong></label>
                  <p>{{ formatDuration(commandDetails.summary.min_duration_seconds) }}</p>
                </div>
                <div class="form-group">
                  <label><strong>Max Duration:</strong></label>
                  <p>{{ formatDuration(commandDetails.summary.max_duration_seconds) }}</p>
                </div>
              </div>
            </div>
          </card>

          <!-- Last Successful Run -->
          <card v-if="commandDetails.last_successful_run" class="mb-4">
            <h4 slot="header" class="card-title text-success">
              <i class="fas fa-check-circle"></i> Last Successful Run
            </h4>
            <div class="row">
              <div class="col-md-3">
                <div class="form-group">
                  <label><strong>ID:</strong></label>
                  <p>{{ commandDetails.last_successful_run.id }}</p>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label><strong>Started:</strong></label>
                  <p>{{ formatDate(commandDetails.last_successful_run.started_at) }}</p>
                </div>
              </div>

              <div class="col-md-3">
                <div class="form-group">
                  <label><strong>Finished:</strong></label>
                  <p>{{ formatDate(commandDetails.last_successful_run.finished_at) }}</p>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label><strong>Duration:</strong></label>
                  <p>{{ formatDuration(commandDetails.last_successful_run.duration_seconds) }}</p>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label><strong>Actions:</strong></label>
                  <base-button
                    type="info"
                    size="sm"
                    @click="viewDetails(commandDetails.last_successful_run.id)"
                  >
                    <i class="fas fa-eye"></i> View Details
                  </base-button>
                </div>
              </div>
            </div>
          </card>

          <!-- Last Failed Run -->
          <card v-if="commandDetails.last_failed_run" class="mb-4">
            <h4 slot="header" class="card-title text-danger">
              <i class="fas fa-times-circle"></i> Last Failed Run
            </h4>
            <div class="row">
              <div class="col-md-3">
                <div class="form-group">
                  <label><strong>ID:</strong></label>
                  <p>{{ commandDetails.last_failed_run.id }}</p>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label><strong>Started:</strong></label>
                  <p>{{ formatDate(commandDetails.last_failed_run.started_at) }}</p>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label><strong>Finished:</strong></label>
                  <p>{{ formatDate(commandDetails.last_failed_run.finished_at) }}</p>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label><strong>Duration:</strong></label>
                  <p>{{ formatDuration(commandDetails.last_failed_run.duration_seconds) }}</p>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label><strong>Actions:</strong></label>
                  <base-button
                    type="info"
                    size="sm"
                    @click="viewDetails(commandDetails.last_failed_run.id)"
                  >
                    <i class="fas fa-eye"></i> View Details
                  </base-button>
                </div>
              </div>
            </div>
            <div v-if="commandDetails.last_failed_run.error_message" class="form-group">
              <label class="text-danger"><strong>Error Message:</strong></label>
              <div class="alert alert-danger">{{ commandDetails.last_failed_run.error_message }}</div>
            </div>
          </card>

          <!-- Recent Runs Table -->
          <card v-if="commandDetails.runs && commandDetails.runs.data && commandDetails.runs.data.length > 0">
            <h4 slot="header" class="card-title">Recent Runs</h4>
            <vue-good-table
              :columns="runsColumns"
              :rows="commandDetails.runs.data"
              :line-numbers="false"
              :search-options="{
                enabled: true,
                placeholder: 'Search runs',
              }"
              :pagination-options="{
                enabled: true,
                perPage: 15,
              }"
              styleClass="vgt-table nocturnal"
              theme="nocturnal"
              :totalRows="commandDetails.runs.total"
              :isLoading="runsLoading"
              mode="remote"
              @on-page-change="onPageChange"
            >
              <template slot="table-row" slot-scope="props">
                <span v-if="props.column.field === 'status'">
                  <span
                    class="badge"
                    :class="{
                      'badge-success': props.row.status === 'success',
                      'badge-danger': props.row.status === 'failed',
                      'badge-warning': props.row.status === 'running'
                    }"
                  >
                    {{ props.row.status }}
                  </span>
                </span>
                <span v-else-if="props.column.field === 'started_at'">
                  {{ formatDate(props.row.started_at) }}
                </span>
                <span v-else-if="props.column.field === 'finished_at'">
                  {{ formatDate(props.row.finished_at) }}
                </span>
                <span v-else-if="props.column.field === 'duration_seconds'">
                  <span v-if="props.row.duration_seconds">
                    {{ formatDuration(props.row.duration_seconds) }}
                  </span>
                  <span v-else class="text-white">
                    <i class="fas fa-spinner spin"></i> Running
                  </span>
                </span>
                <span v-else-if="props.column.field === 'actions'">
                  <base-button
                    type="info"
                    size="sm"
                    @click="viewDetails(props.row.id)"
                  >
                    <i class="fas fa-eye"></i>
                  </base-button>
                </span>
                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </card>
        </div>

        <div v-else class="text-center">
          <card>
            <h4 class="text-danger">Command not found</h4>
            <p>The specified command could not be found or has no run history.</p>
          </card>
        </div>
      </div>
    </div>

    <!-- Run Details Modal -->
    <modal :show.sync="showDetailsModal" body-classes="p-0" modal-classes="modal-lg">
      <card type="secondary" shadow header-classes="bg-white pb-5" body-classes="px-lg-5 py-lg-5" class="border-0 mb-0">
        <template>
          <div class="text-center mb-4">
            <h3>System Run Details</h3>
            <span
              class="badge badge-pill"
              :class="{
                'badge-success': selectedRun.status === 'success',
                'badge-danger': selectedRun.status === 'failed',
                'badge-warning': selectedRun.status === 'running'
              }"
            >
              {{ selectedRun.status }}
            </span>
          </div>

          <div v-if="selectedRun.id">
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>ID:</strong></label>
                  <p>{{ selectedRun.id }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Base Name:</strong></label>
                  <p><code>{{ selectedRun.base_name }}</code></p>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label><strong>Command:</strong></label>
              <p><code>{{ selectedRun.command_name }}</code></p>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Started At:</strong></label>
                  <p>{{ formatDate(selectedRun.started_at) }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label><strong>Finished At:</strong></label>
                  <p v-if="selectedRun.finished_at">{{ formatDate(selectedRun.finished_at) }}</p>
                  <p v-else class="text-white">Still running</p>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label><strong>Duration:</strong></label>
              <p v-if="selectedRun.duration_seconds">{{ formatDuration(selectedRun.duration_seconds) }}</p>
              <p v-else class="text-white">N/A</p>
            </div>

            <div class="form-group" v-if="selectedRun.metadata">
              <label><strong>Metadata:</strong></label>
              <pre class="bg-dark p-3 rounded">{{ JSON.stringify(selectedRun.metadata, null, 2) }}</pre>
            </div>

            <div class="form-group" v-if="selectedRun.error_message">
              <label class="text-danger"><strong>Error Message:</strong></label>
              <div class="alert alert-danger">{{ selectedRun.error_message }}</div>
            </div>

            <div class="form-group" v-if="selectedRun.exception_trace">
              <label class="text-danger"><strong>Exception Trace:</strong></label>
              <pre class="bg-dark p-3 rounded text-danger" style="max-height: 300px; overflow-y: auto;">{{ selectedRun.exception_trace }}</pre>
            </div>
          </div>

          <div class="text-center mt-4">
            <base-button type="secondary" @click="showDetailsModal = false">Close</base-button>
          </div>
        </template>
      </card>
    </modal>
  </div>
</template>

<script>
import axios from 'axios';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { Card, BaseButton, Modal } from '@/components';

export default {
  name: 'CommandDetails',
  components: {
    VueGoodTable,
    Card,
    BaseButton,
    Modal,
  },
  data() {
    return {
      loading: false,
      runsLoading: false,
      commandDetails: {
        command_name: '',
        formatted_name: '',
        period_hours: 24,
        summary: {
          total_runs: 0,
          success_count: 0,
          failed_count: 0,
          running_count: 0,
          interrupted_count: 0,
          success_rate: 0,
          avg_duration_seconds: 0,
          min_duration_seconds: 0,
          max_duration_seconds: 0,
        },
        last_successful_run: null,
        last_failed_run: null,
        runs: {
          data: [],
          current_page: 1,
          last_page: 1,
          per_page: 15,
          total: 0,
        },
      },
      runsColumns: [
        {
          label: 'ID',
          field: 'id',
          type: 'number',
          sortable: false,
        },
        {
          label: 'Started At',
          field: 'started_at',
          sortable: false,
        },
        {
          label: 'Finished At',
          field: 'finished_at',
          sortable: false,
        },
        {
          label: 'Duration',
          field: 'duration_seconds',
          sortable: false,
        },
        {
          label: 'Status',
          field: 'status',
          sortable: false,
        },
        {
          label: 'Actions',
          field: 'actions',
          sortable: false,
        },
      ],
      showDetailsModal: false,
      selectedRun: {},
    };
  },
  computed: {
    commandName() {
      return this.$route.params.commandName;
    },
  },
  mounted() {
    this.fetchCommandDetails();
  },
  watch: {
    commandName(newCommand) {
      if (newCommand) {
        this.fetchCommandDetails();
      }
    },
  },
  methods: {
    async fetchCommandDetails() {
      if (!this.commandName) return;

      this.loading = true;
      try {
        const response = await axios.get('/system-runs/command-details', {
          params: {
            command_name: this.commandName,
            hours: 24,
            per_page: 15
          }
        });
        this.commandDetails = response.data;
      } catch (error) {
        console.error('Error fetching command details:', error);
        this.$toast.error('Failed to fetch command details');
      } finally {
        this.loading = false;
      }
    },
    async viewDetails(id) {
      try {
        this.runsLoading = true;
        const response = await axios.get(`/system-runs/${id}`);
        this.selectedRun = response.data;
        this.showDetailsModal = true;
      } catch (error) {
        console.error('Error fetching run details:', error);
        this.$toast.error('Failed to fetch run details');
      } finally {
        this.runsLoading = false;
      }
    },
    onPageChange(params) {
      this.fetchRuns(params.currentPage);
    },
    async fetchRuns(page = 1) {
      if (!this.commandName) return;

      try {
        this.runsLoading = true;
        const response = await axios.get('/system-runs/command-details', {
          params: {
            command_name: this.commandName,
            hours: 24,
            per_page: 15,
            page: page
          }
        });
        this.commandDetails.runs = response.data.runs;
      } catch (error) {
        console.error('Error fetching command runs:', error);
        this.$toast.error('Failed to fetch command runs');
      } finally {
        this.runsLoading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleString();
    },
    formatDuration(seconds) {
      if (!seconds || seconds === null) return 'N/A';
      const sec = parseFloat(seconds);
      if (sec < 60) {
        return `${sec.toFixed(2)}s`;
      } else if (sec < 3600) {
        const mins = Math.floor(sec / 60);
        const secs = (sec % 60).toFixed(0);
        return `${mins}m ${secs}s`;
      } else {
        const hours = Math.floor(sec / 3600);
        const mins = Math.floor((sec % 3600) / 60);
        return `${hours}h ${mins}m`;
      }
    },
  },
};
</script>

<style scoped>
/* Stats Cards */
.icon-big {
  font-size: 3em;
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-big i {
  line-height: 1;
}

.numbers {
  text-align: right;
  font-size: 2em;
}

.numbers .card-category {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  margin-bottom: 0;
  text-transform: uppercase;
  font-weight: 400;
}

.numbers .card-title {
  margin: 0;
  font-size: 1.7em;
  font-weight: 300;
  color: #ffffff;
}

.card-stats .card-footer {
  padding: 0.75rem 1.5rem;
  margin-top: 1rem;
}

.card-stats .card-footer hr {
  margin-top: 0;
  margin-bottom: 0.5rem;
}

.card-stats .card-footer .stats {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
}

.card-stats .card-footer .stats i {
  margin-right: 5px;
}

/* Gradient backgrounds */
.gradient-primary {
  background: linear-gradient(to bottom left, #1d8cf8, #3358f4, #1d8cf8);
}

.gradient-success {
  background: linear-gradient(to bottom left, #00bf9a, #00b09b, #00bf9a);
}

.gradient-danger {
  background: linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93);
}

.gradient-warning {
  background: linear-gradient(to bottom left, #ff8d72, #ff6491, #ff8d72);
}

/* Spin animation */
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
